import type { ReactNode } from 'react';
import { ArrowRight, FileText, Sparkles, UserRound } from 'lucide-react';
import styles from './PipelineLabPage.module.css';

type StageTone = 'green' | 'blue' | 'amber' | 'coral' | 'default';

type StageFlow = 'intake' | 'ai-screen' | 'ai-interview' | 'human-review' | 'human-only' | 'offer' | 'terminal';

type Stage = {
    key: string;
    name: string;
    count: number;
    tone: StageTone;
    flow?: StageFlow;
};

const STAGES: Stage[] = [
    { key: 'applied', name: 'Applied', count: 0, tone: 'green' },
    { key: 'ai-screen', name: 'AI Screen', count: 0, tone: 'blue' },
    { key: 'offer', name: 'Offer', count: 0, tone: 'amber' },
];

const STAGES_DEFAULT_TONE: Stage[] = STAGES.map((s) => ({ ...s, tone: 'default' as StageTone }));

const STAGES_7: Stage[] = [
    { key: 'applied', name: 'Applied', count: 76, tone: 'green', flow: 'intake' },
    { key: 'ai-screen', name: 'AI Screen', count: 42, tone: 'blue', flow: 'ai-screen' },
    { key: 'phone', name: 'Phone Screen', count: 18, tone: 'amber', flow: 'human-review' },
    { key: 'interview', name: 'Interview', count: 9, tone: 'blue', flow: 'ai-interview' },
    { key: 'hm-review', name: 'HM Review', count: 5, tone: 'amber', flow: 'human-only' },
    { key: 'offer', name: 'Offer', count: 2, tone: 'coral', flow: 'offer' },
    { key: 'hired', name: 'Hired', count: 1, tone: 'green', flow: 'terminal' },
];

const REVIEWERS: Array<{ id: string; initials: string; bg: string }> = [
    { id: 'jt', initials: 'JT', bg: '#FD775E' },
    { id: 'mr', initials: 'MR', bg: '#7AAFC8' },
];

const SOURCES = [
    { src: '/assets/icons/logo_seek.png', alt: 'SEEK' },
    { src: '/assets/icons/logo_linkedin.svg', alt: 'LinkedIn' },
    { src: '/assets/icons/logo_indeed.svg', alt: 'Indeed' },
];

function ToneDot({ tone }: { tone: StageTone }) {
    return <span className={styles.stageDot} data-tone={tone} aria-hidden="true" />;
}

function AvatarStack({ size = 22 }: { size?: number }) {
    return (
        <span className={styles.avatarStack} style={{ ['--av-size' as string]: `${size}px` }}>
            {REVIEWERS.map((r) => (
                <span key={r.id} className={styles.avatar} style={{ background: r.bg }}>
                    {r.initials}
                </span>
            ))}
        </span>
    );
}

function SourceLogos({ size = 28 }: { size?: number }) {
    return (
        <span className={styles.sourceLogos} style={{ ['--logo-size' as string]: `${size}px` }}>
            {SOURCES.map((s) => (
                <img key={s.alt} src={s.src} alt={s.alt} loading="lazy" />
            ))}
        </span>
    );
}

function CardShell({ title, children }: { title: string; children: ReactNode }) {
    return (
        <article className={styles.cardShell}>
            <header className={styles.cardHeader}>
                <span className={styles.cardEyebrow}><span className={styles.eyebrowDot} />Drafting</span>
                <h3 className={styles.cardTitle}>{title}</h3>
                <span className={styles.cardMeta}>0 candidates · 0 automations</span>
            </header>
            {children}
        </article>
    );
}

export function PipelineLabPage() {
    return (
        <div className={styles.page}>
            <header className={styles.pageHeader}>
                <h1>Pipeline view lab</h1>
                <p>
                    Five layouts for the automation view that sits inside a job card on /v2/jobs. All show the same pipeline (Applied →
                    AI Screen → Offer). Pick what reads fastest at a glance.
                </p>
            </header>

            <Variant
                label="A — Compact columns"
                note="Current shape, tightened. Smaller pills (36px), shorter dashed connector, inline outcome chips, avatars at 20px. Same mental model, ~half the height."
                Render={VariantA}
            />

            <Variant
                label="B — Horizontal stage rows"
                note="Stages stack vertically as rows, automations flow left → right inside each row. Reads like a sentence per stage. No floating legend; legend folds into the title row."
                Render={VariantB}
            />

            <Variant
                label="C — Tinted lanes, single accent"
                note="Each stage gets a warm cream tint. One coral accent everywhere — no orange/blue clash. Outcomes become tiny color dots. Connector becomes a thin solid line."
                Render={VariantC}
            />

            <Variant
                label="D — Outline diagram"
                note="Monochrome diagram styling. 1px gray pill borders, no fills. Coral accent only on the active verb. Stage tone shows in a 4px header band, not the whole pill."
                Render={VariantD}
            />

            <Variant
                label="E — Sentence lines"
                note="Most compact. Each stage = one or two readable sentences with inline tag-style chips. Skim the whole pipeline in 5 seconds."
                Render={VariantE}
            />

            <div className={styles.familyDivider}>
                <span>C family — solving the problem: how to surface human-in-loop without breaking C's cohesion</span>
            </div>

            <Variant
                label="C1 — Blue-bordered human pill in the branch row"
                note="Plain dot rows for Pass/Fail; the Parking row is replaced with a real blue pill (avatars + label) so the human step pops at a glance."
                Render={VariantC1}
            />

            <Variant
                label="C1 — 7 stages (realistic pipeline)"
                note="Same C1 layout, full Applied → AI Screen → Phone → Interview → HM Review → Offer → Hired flow. Card scrolls horizontally; the human-in-loop steps (Phone, Interview, HM Review) all surface clearly."
                Render={VariantC1Seven}
            />

            <Variant
                label="C1 (default tones) — what most jobs will actually look like"
                note="Same C1 layout, but all stages are the default warm-gray tone (no green/blue/amber). Lanes lose their per-stage tint but the AI verbs (coral) and Human pill (blue) still pop because they're not stage-tone driven."
                Render={VariantC1Default}
            />

            <Variant
                label="C1 (default + position tint) — calm gradient cool → warm"
                note="Same as default tones, but each lane gets a faint position-based cream: cool at the start of the pipeline, warmer toward the end. No bright color, just enough variation that lanes don't blur together."
                Render={VariantC1DefaultPos}
            />

            <Variant
                label="C1 (default + position tint + numeric kicker) — strongest stage identity without color"
                note="Adds a tiny numbered chip (1, 2, 3) before the stage name so the order is unambiguous. Combined with the position tint, lanes are distinct without ever leaving the warm-gray palette."
                Render={VariantC1DefaultOrder}
            />

            <Variant
                label="C2 — Soft-blue filled human chip"
                note="The Parking row collapses into one solid soft-blue chip with avatars and label. Lighter than a bordered pill but still clearly a different surface."
                Render={VariantC2}
            />

            <Variant
                label="C3 — Lifted Human Review card under the branches"
                note="AI outcomes stay as quick dot rows. Below them, a separate ‘Human review’ mini-card holds the reviewer assignment — a clear handoff zone."
                Render={VariantC3}
            />

            <Variant
                label="C4 — Side-rail emphasis"
                note="All branches stay as compact rows but the Parking row gets a thick blue left rail, a soft blue background, and avatars. Same row height, different gravity."
                Render={VariantC4}
            />

            <Variant
                label="C5 — Bisected lane (AI zone / Human zone)"
                note="The AI Screen lane splits into two zones — a cream ‘AI pre-screen’ area and a light-blue ‘Human review’ area. The Parking branch literally lands in the human zone."
                Render={VariantC5}
            />
        </div>
    );
}

function Variant({
    label,
    note,
    Render,
}: {
    label: string;
    note: string;
    Render: () => ReactNode;
}) {
    return (
        <section className={styles.variant}>
            <div className={styles.variantHeader}>
                <h2>{label}</h2>
                <p>{note}</p>
            </div>
            <div className={styles.variantBody}>{Render()}</div>
        </section>
    );
}

/* ════════════════════════════════════════════════════════════════════
 * A — Compact columns
 * ════════════════════════════════════════════════════════════════════ */

function VariantA() {
    return (
        <CardShell title="Tighten Pipeline to 3 Stages">
            <div className={styles.aBody}>
                <div className={styles.aGrid}>
                    {STAGES.map((stage) => (
                        <div key={stage.key} className={styles.aColumn}>
                            <div className={styles.aStageHead}>
                                <ToneDot tone={stage.tone} />
                                <strong>{stage.name}</strong>
                                <span className={styles.aCount}>{stage.count}</span>
                            </div>
                            <AColumnFlow stageKey={stage.key} />
                        </div>
                    ))}
                </div>
                <div className={styles.aLegend}>
                    <span><i className={styles.legendAi} />AI Automation</span>
                    <span><i className={styles.legendHuman} />Human in the loop</span>
                </div>
            </div>
        </CardShell>
    );
}

function AColumnFlow({ stageKey }: { stageKey: string }) {
    if (stageKey === 'applied') {
        return (
            <div className={styles.aLane}>
                <div className={styles.aPillAi}>
                    <SourceLogos size={22} />
                </div>
                <DashedDown label="Enter Applied" />
                <div className={styles.aPillAi}>
                    <ArrowRight size={14} strokeWidth={2} />
                    <span>Move to <strong>AI Screen</strong></span>
                </div>
            </div>
        );
    }
    if (stageKey === 'ai-screen') {
        return (
            <div className={styles.aLane}>
                <div className={styles.aPillAi}>
                    <Sparkles size={14} strokeWidth={2} />
                    <span>Auto-start screening on enter</span>
                </div>
                <DashedDown />
                <div className={styles.aBranches}>
                    <span className={`${styles.aChip} ${styles.aChipPass}`}>PASS</span>
                    <div className={styles.aPillAi}>
                        <ArrowRight size={14} strokeWidth={2} />
                        <span>Move to <strong>Offer</strong></span>
                    </div>
                    <span className={`${styles.aChip} ${styles.aChipPark}`}>PARKING LOT</span>
                    <div className={styles.aPillHuman}>
                        <AvatarStack size={20} />
                        <span>Assign <strong>2 Reviewers</strong></span>
                    </div>
                    <span className={`${styles.aChip} ${styles.aChipFail}`}>FAIL</span>
                    <div className={styles.aPillAi}>
                        <ArrowRight size={14} strokeWidth={2} />
                        <span>Move to <strong>Rejected</strong></span>
                    </div>
                </div>
            </div>
        );
    }
    return (
        <div className={styles.aLane}>
            <div className={styles.aPillAi}>
                <FileText size={14} strokeWidth={2} />
                <span>Prepare <strong>offer</strong></span>
            </div>
        </div>
    );
}

function DashedDown({ label }: { label?: string }) {
    return (
        <div className={styles.aDashed} aria-hidden="true">
            {label ? <span>Enter <strong>{label.replace(/^Enter\s+/, '')}</strong></span> : null}
        </div>
    );
}

/* ════════════════════════════════════════════════════════════════════
 * B — Horizontal stage rows
 * ════════════════════════════════════════════════════════════════════ */

function VariantB() {
    return (
        <CardShell title="Tighten Pipeline to 3 Stages">
            <div className={styles.bBody}>
                <div className={styles.bLegend}>
                    <span><i className={styles.legendAi} />AI Automation</span>
                    <span><i className={styles.legendHuman} />Human in the loop</span>
                </div>
                <div className={styles.bRows}>
                    {STAGES.map((stage) => (
                        <BRow key={stage.key} stage={stage} />
                    ))}
                </div>
            </div>
        </CardShell>
    );
}

function BRow({ stage }: { stage: Stage }) {
    return (
        <div className={styles.bRow}>
            <div className={styles.bStage} data-tone={stage.tone}>
                <ToneDot tone={stage.tone} />
                <strong>{stage.name}</strong>
                <span className={styles.bCount}>{stage.count}</span>
            </div>
            <div className={styles.bFlow}>
                {stage.key === 'applied' ? (
                    <>
                        <span className={styles.bPillAi}><SourceLogos size={20} /></span>
                        <BSep />
                        <span className={styles.bPillAi}><ArrowRight size={13} />Move to <strong>AI Screen</strong></span>
                    </>
                ) : null}
                {stage.key === 'ai-screen' ? (
                    <>
                        <span className={styles.bPillAi}><Sparkles size={13} />Auto-start screening</span>
                        <BSep />
                        <span className={styles.bBranchGroup}>
                            <span className={styles.bBranchRow}>
                                <span className={`${styles.bChip} ${styles.aChipPass}`}>PASS</span>
                                <span className={styles.bPillAi}><ArrowRight size={13} />Move to <strong>Offer</strong></span>
                            </span>
                            <span className={styles.bBranchRow}>
                                <span className={`${styles.bChip} ${styles.aChipPark}`}>PARKING</span>
                                <span className={styles.bPillHuman}><AvatarStack size={18} />Assign <strong>2 Reviewers</strong></span>
                            </span>
                            <span className={styles.bBranchRow}>
                                <span className={`${styles.bChip} ${styles.aChipFail}`}>FAIL</span>
                                <span className={styles.bPillAi}><ArrowRight size={13} />Move to <strong>Rejected</strong></span>
                            </span>
                        </span>
                    </>
                ) : null}
                {stage.key === 'offer' ? (
                    <span className={styles.bPillAi}><FileText size={13} />Prepare <strong>offer</strong></span>
                ) : null}
            </div>
        </div>
    );
}

function BSep() {
    return <ArrowRight size={14} className={styles.bSep} aria-hidden="true" />;
}

/* ════════════════════════════════════════════════════════════════════
 * C — Tinted lanes, single accent
 * ════════════════════════════════════════════════════════════════════ */

function VariantC() {
    return (
        <CardShell title="Tighten Pipeline to 3 Stages">
            <div className={styles.cBody}>
                <div className={styles.cGrid}>
                    {STAGES.map((stage) => (
                        <div key={stage.key} className={styles.cLane} data-tone={stage.tone}>
                            <div className={styles.cStageHead}>
                                <ToneDot tone={stage.tone} />
                                <strong>{stage.name}</strong>
                                <span className={styles.cCount}>{stage.count}</span>
                            </div>
                            <CColumnFlow stageKey={stage.key} />
                        </div>
                    ))}
                </div>
                <div className={styles.cLegend}>
                    <span><i className={styles.cDotAi} />AI Automation</span>
                    <span><i className={styles.cDotHuman} />Human in the loop</span>
                </div>
            </div>
        </CardShell>
    );
}

function CColumnFlow({ stageKey }: { stageKey: string }) {
    if (stageKey === 'applied') {
        return (
            <div className={styles.cLaneFlow}>
                <div className={styles.cNode}>
                    <SourceLogos size={20} />
                </div>
                <SolidDown />
                <div className={styles.cNode}>
                    <ArrowRight size={13} className={styles.cAccent} />
                    Move to <strong>AI Screen</strong>
                </div>
            </div>
        );
    }
    if (stageKey === 'ai-screen') {
        return (
            <div className={styles.cLaneFlow}>
                <div className={styles.cNode}>
                    <Sparkles size={13} className={styles.cAccent} />
                    Auto-start screening
                </div>
                <SolidDown />
                <div className={styles.cBranches}>
                    <div className={styles.cBranchRow}>
                        <span className={`${styles.cBranchDot} ${styles.cDotPass}`} />
                        <span className={styles.cBranchLabel}>Pass</span>
                        <span className={styles.cBranchAction}>→ Offer</span>
                    </div>
                    <div className={styles.cBranchRow}>
                        <span className={`${styles.cBranchDot} ${styles.cDotPark}`} />
                        <span className={styles.cBranchLabel}>Parking lot</span>
                        <span className={styles.cBranchAction}>
                            <AvatarStack size={16} />
                            2 reviewers
                        </span>
                    </div>
                    <div className={styles.cBranchRow}>
                        <span className={`${styles.cBranchDot} ${styles.cDotFail}`} />
                        <span className={styles.cBranchLabel}>Fail</span>
                        <span className={styles.cBranchAction}>→ Rejected</span>
                    </div>
                </div>
            </div>
        );
    }
    return (
        <div className={styles.cLaneFlow}>
            <div className={styles.cNode}>
                <FileText size={13} className={styles.cAccent} />
                Prepare <strong>offer</strong>
            </div>
        </div>
    );
}

function SolidDown() {
    return <span className={styles.cSolidLine} aria-hidden="true" />;
}

/* ════════════════════════════════════════════════════════════════════
 * D — Outline diagram
 * ════════════════════════════════════════════════════════════════════ */

function VariantD() {
    return (
        <CardShell title="Tighten Pipeline to 3 Stages">
            <div className={styles.dBody}>
                <div className={styles.dGrid}>
                    {STAGES.map((stage) => (
                        <div key={stage.key} className={styles.dColumn}>
                            <div className={styles.dStageBand} data-tone={stage.tone} />
                            <div className={styles.dStageHead}>
                                <strong>{stage.name}</strong>
                                <span className={styles.dCount}>{stage.count}</span>
                            </div>
                            <DColumnFlow stageKey={stage.key} />
                        </div>
                    ))}
                </div>
                <div className={styles.dLegend}>
                    <span><Sparkles size={11} />AI</span>
                    <span><UserRound size={11} />Human</span>
                </div>
            </div>
        </CardShell>
    );
}

function DColumnFlow({ stageKey }: { stageKey: string }) {
    if (stageKey === 'applied') {
        return (
            <div className={styles.dLane}>
                <div className={styles.dNode}>
                    <SourceLogos size={18} />
                </div>
                <ThinDown />
                <div className={styles.dNode}>
                    <span className={styles.dVerb}>Move to</span>
                    <strong>AI Screen</strong>
                </div>
            </div>
        );
    }
    if (stageKey === 'ai-screen') {
        return (
            <div className={styles.dLane}>
                <div className={styles.dNode}>
                    <Sparkles size={12} />
                    <span className={styles.dVerb}>Auto-start</span>
                    <strong>screening</strong>
                </div>
                <ThinDown />
                <div className={styles.dBranches}>
                    <div className={styles.dBranchRow}>
                        <span className={`${styles.dResult} ${styles.dResultPass}`}>Pass</span>
                        <span className={styles.dBranchAction}>
                            <span className={styles.dVerb}>Move to</span>
                            <strong>Offer</strong>
                        </span>
                    </div>
                    <div className={styles.dBranchRow}>
                        <span className={`${styles.dResult} ${styles.dResultPark}`}>Parking</span>
                        <span className={styles.dBranchAction}>
                            <UserRound size={12} />
                            <span className={styles.dVerb}>Assign</span>
                            <strong>2 Reviewers</strong>
                            <AvatarStack size={16} />
                        </span>
                    </div>
                    <div className={styles.dBranchRow}>
                        <span className={`${styles.dResult} ${styles.dResultFail}`}>Fail</span>
                        <span className={styles.dBranchAction}>
                            <span className={styles.dVerb}>Move to</span>
                            <strong>Rejected</strong>
                        </span>
                    </div>
                </div>
            </div>
        );
    }
    return (
        <div className={styles.dLane}>
            <div className={styles.dNode}>
                <FileText size={12} />
                <span className={styles.dVerb}>Prepare</span>
                <strong>offer</strong>
            </div>
        </div>
    );
}

function ThinDown() {
    return <span className={styles.dThinLine} aria-hidden="true" />;
}

/* ════════════════════════════════════════════════════════════════════
 * E — Sentence lines
 * ════════════════════════════════════════════════════════════════════ */

function VariantE() {
    return (
        <CardShell title="Tighten Pipeline to 3 Stages">
            <div className={styles.eBody}>
                <div className={styles.eRow}>
                    <span className={styles.eStageBadge} data-tone="green">Applied</span>
                    <p className={styles.eSentence}>
                        Candidates from <ETag><SourceLogos size={16} /></ETag> auto-move to{' '}
                        <ETagAction>AI Screen</ETagAction>.
                    </p>
                </div>
                <div className={styles.eRow}>
                    <span className={styles.eStageBadge} data-tone="blue">AI Screen</span>
                    <p className={styles.eSentence}>
                        Auto-start <ETag><Sparkles size={11} /> screening</ETag> on entry. If{' '}
                        <ETagPass>Pass</ETagPass> → <ETagAction>Offer</ETagAction>; if{' '}
                        <ETagPark>Parking</ETagPark> → <ETagHuman><AvatarStack size={14} /> 2 Reviewers</ETagHuman>; if{' '}
                        <ETagFail>Fail</ETagFail> → <ETagAction>Rejected</ETagAction>.
                    </p>
                </div>
                <div className={styles.eRow}>
                    <span className={styles.eStageBadge} data-tone="amber">Offer</span>
                    <p className={styles.eSentence}>
                        Prepare <ETag><FileText size={11} /> offer</ETag>.
                    </p>
                </div>
            </div>
        </CardShell>
    );
}

function ETag({ children }: { children: ReactNode }) {
    return <span className={`${styles.eTag} ${styles.eTagAi}`}>{children}</span>;
}
function ETagAction({ children }: { children: ReactNode }) {
    return <span className={`${styles.eTag} ${styles.eTagAction}`}>{children}</span>;
}
function ETagHuman({ children }: { children: ReactNode }) {
    return <span className={`${styles.eTag} ${styles.eTagHuman}`}>{children}</span>;
}
function ETagPass({ children }: { children: ReactNode }) {
    return <span className={`${styles.eTag} ${styles.eTagPass}`}>{children}</span>;
}
function ETagPark({ children }: { children: ReactNode }) {
    return <span className={`${styles.eTag} ${styles.eTagPark}`}>{children}</span>;
}
function ETagFail({ children }: { children: ReactNode }) {
    return <span className={`${styles.eTag} ${styles.eTagFail}`}>{children}</span>;
}

/* ════════════════════════════════════════════════════════════════════
 * C family — same base lanes, different ways to surface human work
 * ════════════════════════════════════════════════════════════════════ */

function CBaseShell({ children }: { children: ReactNode }) {
    return (
        <CardShell title="Tighten Pipeline to 3 Stages">
            <div className={styles.cBody}>
                <div className={styles.cGrid}>{children}</div>
                <div className={styles.cLegend}>
                    <span><i className={styles.cDotAi} />AI Automation</span>
                    <span><i className={styles.cDotHuman} />Human in the loop</span>
                </div>
            </div>
        </CardShell>
    );
}

function CLane({
    stage,
    extraClass,
    showOrder,
    order,
    children,
}: {
    stage: Stage;
    extraClass?: string;
    showOrder?: boolean;
    order?: number;
    children: ReactNode;
}) {
    return (
        <div className={`${styles.cLane}${extraClass ? ' ' + extraClass : ''}`} data-tone={stage.tone}>
            <div className={styles.cStageHead}>
                {showOrder && order != null ? (
                    <span className={styles.cOrder}>{order + 1}</span>
                ) : (
                    <ToneDot tone={stage.tone} />
                )}
                <strong>{stage.name}</strong>
                <span className={styles.cCount}>{stage.count}</span>
            </div>
            {children}
        </div>
    );
}

function CAppliedFlow() {
    return (
        <div className={styles.cLaneFlow}>
            <div className={styles.cNode}>
                <SourceLogos size={20} />
            </div>
            <SolidDown />
            <div className={styles.cNode}>
                <ArrowRight size={13} className={styles.cAccent} />
                Move to <strong>AI Screen</strong>
            </div>
        </div>
    );
}

function COfferFlow() {
    return (
        <div className={styles.cLaneFlow}>
            <div className={styles.cNode}>
                <FileText size={13} className={styles.cAccent} />
                Prepare <strong>offer</strong>
            </div>
        </div>
    );
}

function CScreenHeader() {
    return (
        <>
            <div className={styles.cNode}>
                <Sparkles size={13} className={styles.cAccent} />
                Auto-start screening
            </div>
            <SolidDown />
        </>
    );
}

/* C1 — Blue-bordered human pill in the branch row */
function VariantC1() {
    return <C1Body stages={STAGES} />;
}

function VariantC1Default() {
    return <C1Body stages={STAGES_DEFAULT_TONE} />;
}

function VariantC1DefaultPos() {
    return <C1Body stages={STAGES_DEFAULT_TONE} positionTint />;
}

function VariantC1DefaultOrder() {
    return <C1Body stages={STAGES_DEFAULT_TONE} positionTint showOrder />;
}

function C1Body({
    stages,
    positionTint,
    showOrder,
}: {
    stages: Stage[];
    positionTint?: boolean;
    showOrder?: boolean;
}) {
    return (
        <CBaseShell>
            {stages.map((stage, idx) => (
                <CLane
                    key={stage.key}
                    stage={stage}
                    extraClass={positionTint ? styles[`cLanePos${idx}`] : undefined}
                    showOrder={showOrder}
                    order={idx}
                >
                    {stage.key === 'applied' ? <CAppliedFlow /> : null}
                    {stage.key === 'offer' ? <COfferFlow /> : null}
                    {stage.key === 'ai-screen' ? (
                        <div className={styles.cLaneFlow}>
                            <CScreenHeader />
                            <div className={styles.cBranches}>
                                <div className={styles.cBranchRow}>
                                    <span className={`${styles.cBranchDot} ${styles.cDotPass}`} />
                                    <span className={styles.cBranchLabel}>Pass</span>
                                    <span className={styles.cBranchAction}>→ Offer</span>
                                </div>
                                <div className={styles.c1ParkingRow}>
                                    <span className={`${styles.cBranchDot} ${styles.cDotPark}`} />
                                    <span className={styles.cBranchLabel}>Parking</span>
                                    <span className={styles.c1HumanPill}>
                                        <AvatarStack size={18} />
                                        Assign <strong>2 Reviewers</strong>
                                    </span>
                                </div>
                                <div className={styles.cBranchRow}>
                                    <span className={`${styles.cBranchDot} ${styles.cDotFail}`} />
                                    <span className={styles.cBranchLabel}>Fail</span>
                                    <span className={styles.cBranchAction}>→ Rejected</span>
                                </div>
                            </div>
                        </div>
                    ) : null}
                </CLane>
            ))}
        </CBaseShell>
    );
}

/* C1 — 7-stage realistic pipeline */
function VariantC1Seven() {
    return (
        <CardShell title="Senior Backend Engineer, Payments">
            <div className={`${styles.cBody} ${styles.cBodyScroll}`}>
                <div className={`${styles.cGrid} ${styles.cGrid7}`}>
                    {STAGES_7.map((stage, idx) => (
                        <CLane key={stage.key} stage={stage}>
                            <C7Flow stage={stage} stages={STAGES_7} index={idx} />
                        </CLane>
                    ))}
                </div>
                <div className={styles.cLegend}>
                    <span><i className={styles.cDotAi} />AI Automation</span>
                    <span><i className={styles.cDotHuman} />Human in the loop</span>
                </div>
            </div>
        </CardShell>
    );
}

function C7Flow({ stage, stages, index }: { stage: Stage; stages: Stage[]; index: number }) {
    const next = stages[index + 1];
    const flow = stage.flow;

    if (flow === 'intake') {
        return (
            <div className={styles.cLaneFlow}>
                <div className={styles.cNode}>
                    <SourceLogos size={20} />
                </div>
                <SolidDown />
                <div className={styles.cNode}>
                    <ArrowRight size={13} className={styles.cAccent} />
                    Move to <strong>{next?.name}</strong>
                </div>
            </div>
        );
    }

    if (flow === 'ai-screen') {
        return (
            <div className={styles.cLaneFlow}>
                <div className={styles.cNode}>
                    <Sparkles size={13} className={styles.cAccent} />
                    Auto-start screening
                </div>
                <SolidDown />
                <div className={styles.cBranches}>
                    <div className={styles.cBranchRow}>
                        <span className={`${styles.cBranchDot} ${styles.cDotPass}`} />
                        <span className={styles.cBranchLabel}>Pass</span>
                        <span className={styles.cBranchAction}>→ {next?.name}</span>
                    </div>
                    <div className={styles.c1ParkingRow}>
                        <span className={`${styles.cBranchDot} ${styles.cDotPark}`} />
                        <span className={styles.cBranchLabel}>Parking</span>
                        <span className={styles.c1HumanPill}>
                            <AvatarStack size={18} />
                            Assign <strong>2 Reviewers</strong>
                        </span>
                    </div>
                    <div className={styles.cBranchRow}>
                        <span className={`${styles.cBranchDot} ${styles.cDotFail}`} />
                        <span className={styles.cBranchLabel}>Fail</span>
                        <span className={styles.cBranchAction}>→ Rejected</span>
                    </div>
                </div>
            </div>
        );
    }

    if (flow === 'human-review') {
        return (
            <div className={styles.cLaneFlow}>
                <div className={styles.cNode}>
                    <Sparkles size={13} className={styles.cAccent} />
                    Auto-schedule call
                </div>
                <SolidDown />
                <span className={styles.c1HumanPill}>
                    <AvatarStack size={18} />
                    Assign <strong>1 Recruiter</strong>
                </span>
            </div>
        );
    }

    if (flow === 'ai-interview') {
        return (
            <div className={styles.cLaneFlow}>
                <div className={styles.cNode}>
                    <Sparkles size={13} className={styles.cAccent} />
                    Auto-invite to AI interview
                </div>
                <SolidDown />
                <span className={styles.c1HumanPill}>
                    <AvatarStack size={18} />
                    Assign <strong>2 Reviewers</strong>
                </span>
            </div>
        );
    }

    if (flow === 'human-only') {
        return (
            <div className={styles.cLaneFlow}>
                <span className={styles.c1HumanPill}>
                    <AvatarStack size={18} />
                    <strong>Hiring manager</strong>
                </span>
                <SolidDown />
                <div className={styles.cNode}>
                    <ArrowRight size={13} className={styles.cAccent} />
                    Move to <strong>{next?.name}</strong>
                </div>
            </div>
        );
    }

    if (flow === 'offer') {
        return (
            <div className={styles.cLaneFlow}>
                <div className={styles.cNode}>
                    <FileText size={13} className={styles.cAccent} />
                    Prepare <strong>offer</strong>
                </div>
                <SolidDown />
                <div className={styles.cNode}>
                    <ArrowRight size={13} className={styles.cAccent} />
                    Move to <strong>{next?.name}</strong>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.cLaneFlow}>
            <div className={`${styles.cNode} ${styles.cNodeMuted}`}>
                <span>Terminal stage</span>
            </div>
        </div>
    );
}

/* C2 — Soft-blue filled human chip */
function VariantC2() {
    return (
        <CBaseShell>
            {STAGES.map((stage) => (
                <CLane key={stage.key} stage={stage}>
                    {stage.key === 'applied' ? <CAppliedFlow /> : null}
                    {stage.key === 'offer' ? <COfferFlow /> : null}
                    {stage.key === 'ai-screen' ? (
                        <div className={styles.cLaneFlow}>
                            <CScreenHeader />
                            <div className={styles.cBranches}>
                                <div className={styles.cBranchRow}>
                                    <span className={`${styles.cBranchDot} ${styles.cDotPass}`} />
                                    <span className={styles.cBranchLabel}>Pass</span>
                                    <span className={styles.cBranchAction}>→ Offer</span>
                                </div>
                                <div className={styles.c2ParkingChip}>
                                    <span className={styles.c2ParkingTag}>Parking</span>
                                    <AvatarStack size={18} />
                                    <span className={styles.c2HumanText}>Assign <strong>2 Reviewers</strong></span>
                                </div>
                                <div className={styles.cBranchRow}>
                                    <span className={`${styles.cBranchDot} ${styles.cDotFail}`} />
                                    <span className={styles.cBranchLabel}>Fail</span>
                                    <span className={styles.cBranchAction}>→ Rejected</span>
                                </div>
                            </div>
                        </div>
                    ) : null}
                </CLane>
            ))}
        </CBaseShell>
    );
}

/* C3 — Lifted Human Review card under the branches */
function VariantC3() {
    return (
        <CBaseShell>
            {STAGES.map((stage) => (
                <CLane key={stage.key} stage={stage}>
                    {stage.key === 'applied' ? <CAppliedFlow /> : null}
                    {stage.key === 'offer' ? <COfferFlow /> : null}
                    {stage.key === 'ai-screen' ? (
                        <div className={styles.cLaneFlow}>
                            <CScreenHeader />
                            <div className={styles.cBranches}>
                                <div className={styles.cBranchRow}>
                                    <span className={`${styles.cBranchDot} ${styles.cDotPass}`} />
                                    <span className={styles.cBranchLabel}>Pass</span>
                                    <span className={styles.cBranchAction}>→ Offer</span>
                                </div>
                                <div className={styles.cBranchRow}>
                                    <span className={`${styles.cBranchDot} ${styles.cDotFail}`} />
                                    <span className={styles.cBranchLabel}>Fail</span>
                                    <span className={styles.cBranchAction}>→ Rejected</span>
                                </div>
                            </div>
                            <div className={styles.c3HumanCard}>
                                <div className={styles.c3HumanHeader}>
                                    <UserRound size={11} />
                                    <span>Human review · Parking lot</span>
                                </div>
                                <div className={styles.c3HumanBody}>
                                    <AvatarStack size={22} />
                                    <span>Assign <strong>2 Reviewers</strong></span>
                                </div>
                            </div>
                        </div>
                    ) : null}
                </CLane>
            ))}
        </CBaseShell>
    );
}

/* C4 — Side-rail emphasis */
function VariantC4() {
    return (
        <CBaseShell>
            {STAGES.map((stage) => (
                <CLane key={stage.key} stage={stage}>
                    {stage.key === 'applied' ? <CAppliedFlow /> : null}
                    {stage.key === 'offer' ? <COfferFlow /> : null}
                    {stage.key === 'ai-screen' ? (
                        <div className={styles.cLaneFlow}>
                            <CScreenHeader />
                            <div className={styles.cBranches}>
                                <div className={`${styles.c4Row} ${styles.c4RowAi}`}>
                                    <span className={`${styles.cBranchDot} ${styles.cDotPass}`} />
                                    <span className={styles.cBranchLabel}>Pass</span>
                                    <span className={styles.cBranchAction}>→ Offer</span>
                                </div>
                                <div className={`${styles.c4Row} ${styles.c4RowHuman}`}>
                                    <span className={`${styles.cBranchDot} ${styles.cDotPark}`} />
                                    <span className={styles.cBranchLabel}>Parking</span>
                                    <span className={styles.c4HumanAction}>
                                        <AvatarStack size={18} />
                                        2 Reviewers
                                    </span>
                                </div>
                                <div className={`${styles.c4Row} ${styles.c4RowAi}`}>
                                    <span className={`${styles.cBranchDot} ${styles.cDotFail}`} />
                                    <span className={styles.cBranchLabel}>Fail</span>
                                    <span className={styles.cBranchAction}>→ Rejected</span>
                                </div>
                            </div>
                        </div>
                    ) : null}
                </CLane>
            ))}
        </CBaseShell>
    );
}

/* C5 — Bisected lane: AI zone (top) / Human zone (bottom) */
function VariantC5() {
    return (
        <CBaseShell>
            {STAGES.map((stage) => {
                if (stage.key !== 'ai-screen') {
                    return (
                        <CLane key={stage.key} stage={stage}>
                            {stage.key === 'applied' ? <CAppliedFlow /> : <COfferFlow />}
                        </CLane>
                    );
                }
                return (
                    <div key={stage.key} className={styles.c5Lane} data-tone={stage.tone}>
                        <div className={styles.cStageHead}>
                            <ToneDot tone={stage.tone} />
                            <strong>{stage.name}</strong>
                            <span className={styles.cCount}>{stage.count}</span>
                        </div>
                        <div className={styles.c5Zones}>
                            <div className={styles.c5AiZone}>
                                <span className={styles.c5ZoneLabel}>
                                    <Sparkles size={10} />AI pre-screen
                                </span>
                                <div className={styles.cNode}>
                                    <Sparkles size={13} className={styles.cAccent} />
                                    Auto-start screening
                                </div>
                                <div className={styles.c5OutcomeRow}>
                                    <span className={`${styles.cBranchDot} ${styles.cDotPass}`} />
                                    <span>Pass → Offer</span>
                                </div>
                                <div className={styles.c5OutcomeRow}>
                                    <span className={`${styles.cBranchDot} ${styles.cDotFail}`} />
                                    <span>Fail → Rejected</span>
                                </div>
                            </div>
                            <div className={styles.c5HumanZone}>
                                <span className={styles.c5ZoneLabelHuman}>
                                    <UserRound size={10} />Human review
                                </span>
                                <div className={styles.c5HumanRow}>
                                    <span className={`${styles.cBranchDot} ${styles.cDotPark}`} />
                                    <span className={styles.c5HumanLabel}>Parking lot</span>
                                    <AvatarStack size={20} />
                                </div>
                                <div className={styles.c5HumanAction}>
                                    Assign <strong>2 Reviewers</strong>
                                </div>
                            </div>
                        </div>
                    </div>
                );
            })}
        </CBaseShell>
    );
}
